import { useRouter } from '@moonpig/web-core-routing'
import { useStoreId } from '@moonpig/web-core-stores'
import { match, useIsMobile } from '@moonpig/web-shared-utils'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { useProductsLocaleText } from '../../locale'
import { ProductInfoProduct } from '../../types'
import { isCardProduct } from '../../utils/isCardProduct'
import { hasMultipleVariants } from '../../utils/hasMultipleVariants'
import { Module } from '../types'
import { E_CARD_SIZE_ID, POSTCARD_SIZE_ID } from '../../constants'
import {
  isDigitalGift,
  isDigitalGiftCard,
} from '../../utils/isDigitalGift/isDigitalGift'
import { createGroupCardProjectDesktopConfiguration } from './group-project/GroupCardProjectDesktop'
import { createGroupCardProjectMobileConfiguration } from './group-project/GroupCardProjectMobile'
import { createCardMultiBuyDesktopConfiguration } from './multibuy/CardMultiBuyDesktop'
import { createCardMultiBuyMobileConfiguration } from './multibuy/CardMultiBuyMobile'
import { createCardOutOfStockDesktopConfiguration } from './out-of-stock/CardOutOfStockDesktop'
import { createCardOutOfStockMobileConfiguration } from './out-of-stock/CardOutOfStockMobile'
import { createCardSimplifiedModalMobileConfiguration } from './simplified-modal/CardSimplifiedModalMobile'
import { createCardSimplifiedModalDesktopConfiguration } from './simplified-modal/CardSimplifiedModalDesktop'
import { createNonCardSingleVariantConfiguration } from './non-card/NonCardSingleVariant'
import { createNonCardMultipleVariantsConfiguration } from './non-card/NonCardMultipleVariants'
import { replacePeopleAlsoViewedWithMoreLikeThis } from './more-like-this/MoreLikeThis'
import { createDigitalGiftCardMultipleVariantsConfiguration } from './non-card/DigitalGiftCard'
import { createDigitalGiftSingleVariantConfiguration } from './non-card/DigitalGift'

type UseDefaultModuleConfiguration = () => (input: {
  product: ProductInfoProduct
}) => Module[]

export const useDefaultModuleConfiguration: UseDefaultModuleConfiguration =
  () => {
    const storeId = useStoreId()
    const localise = useProductsLocaleText()
    const isMobile = useIsMobile()
    const router = useRouter()
    const isSimplifiedModal =
      useExperiment('simplified-modal-and-pdp', {
        fallback: 'control',
      }) === 'treatment'

    const moreLikeThisExperiment = useExperiment(
      'search-use-more-like-this-v3',
      {
        fallback: 'control',
      },
    )
    const showMoreLikeThis =
      moreLikeThisExperiment === 'carousels' ||
      moreLikeThisExperiment === 'tabs'

    return ({ product }) => {
      const isGroupCardProject =
        !!router.getCurrentRoute<'search'>()?.params?.groupCardProject

      const conditions = {
        isCard: isCardProduct(product.category.department),
        isDigitalGift: isDigitalGift(product.category.department),
        isDigitalGiftCard: isDigitalGiftCard(product.category.department),
        inStock: product.variants.some(({ inStock }) => inStock),
        isMobile,
        isGroupCardProject,
        storeId,
        isSimplifiedModal,
        hasECard: !!product.variants.find(({ key }) => key === E_CARD_SIZE_ID),
        hasMultipleVariants: hasMultipleVariants(product.variants),
        isPostcard: product.masterVariant.key === POSTCARD_SIZE_ID,
      }

      const modules = match(conditions)
        .with(
          {
            isDigitalGift: true,
            hasMultipleVariants: false,
          },
          () => createDigitalGiftSingleVariantConfiguration(),
        )
        .with({ isDigitalGiftCard: true, hasMultipleVariants: true }, () =>
          createDigitalGiftCardMultipleVariantsConfiguration(),
        )
        .with({ isCard: false, hasMultipleVariants: false }, () =>
          createNonCardSingleVariantConfiguration(),
        )
        .with({ isCard: false, hasMultipleVariants: true }, () =>
          createNonCardMultipleVariantsConfiguration(),
        )
        .with({ inStock: false, isMobile: true }, () =>
          createCardOutOfStockMobileConfiguration(),
        )
        .with({ inStock: false, isMobile: false }, () =>
          createCardOutOfStockDesktopConfiguration(),
        )
        .with({ isGroupCardProject: true, isMobile: true }, () =>
          createGroupCardProjectMobileConfiguration(),
        )
        .with({ isGroupCardProject: true, isMobile: false }, () =>
          createGroupCardProjectDesktopConfiguration(),
        )
        .with({ isMobile: true, isSimplifiedModal: true }, () =>
          createCardSimplifiedModalMobileConfiguration({
            localise,
            storeId,
            isPostcard: conditions.isPostcard,
          }),
        )
        .with({ isMobile: false, isSimplifiedModal: true }, () =>
          createCardSimplifiedModalDesktopConfiguration({
            localise,
            storeId,
            isPostcard: conditions.isPostcard,
          }),
        )
        .with({ isMobile: true }, () =>
          createCardMultiBuyMobileConfiguration({
            localise,
            storeId,
            isPostcard: conditions.isPostcard,
          }),
        )
        .with({ isMobile: false }, () =>
          createCardMultiBuyDesktopConfiguration({
            localise,
            storeId,
            isPostcard: conditions.isPostcard,
          }),
        )
        .otherwise(/* istanbul ignore next */ () => [])

      return showMoreLikeThis
        ? replacePeopleAlsoViewedWithMoreLikeThis(modules)
        : modules
    }
  }
